import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import AdminPractitionerSearch from '../components/AdminPractitionerSearch';
import SectionWell from './SectionWell';
import EditSection from './EditSection';
import { changeOrderPractitioner, fetchPractitionerOptions } from '../util/apiUtils';

/**
 * Creates a SectionWell that displays an order's practitioner details.
 * When in editing mode, expands to allow switching to a different practitioner.
 *
 * @param gearOrder The Gear Order
 * @param userInfo The logged in user
 * @param isEditing Flag to indicate if editing mode should be displayed
 * @param isEditSaveInProgress Flag to indicate if a save request is in progress
 * @param onEdit Function to call when this component is put into edit mode (When the edit button is clicked)
 * @param onSave Function to call when a new practitioner is saved to the order
 * @param onCnacel Function to call when editing mode is closed without saving
 */
const PractitionerDetails = forwardRef(
	({ gearOrder, userInfo, isEditing, isEditSaveInProgress, onEdit, onSave, onCancel }, ref) => {
		const [practitionerOptions, setPractitionerOptions] = useState([]);
		const [selectedOption, setSelectedOption] = useState({
			practitioner: gearOrder?.practitioner,
			office: gearOrder?.practitioner?.office
		});
		const [showEditButton, setShowEditButton] = useState(false);
		const [changesSaved, setChangesSaved] = useState(false);
		const [error, setError] = useState(null);

		/**
		 * This method builds the ref properties that will be available to the parent. We are exposing
		 * three functions: 'save', 'reset', and 'isDirty'.
		 */
		useImperativeHandle(
			ref,
			() => ({
				save: () => {
					setError(null);
					if (hasSelectionChanged(gearOrder?.practitioner?.gsdlNo, selectedOption?.practitioner?.gsdlNo)) {
						return saveSelection(gearOrder.id, selectedOption.practitioner.gsdlNo)
							.then((gearOrder) => {
								setChangesSaved(true);
								return Promise.resolve(gearOrder);
							})
							.catch((error) => {
								setChangesSaved(false);
								setError(
									'An unexpected error has occurred. Please try again or call us at 800-522-4762 if the issue continues.'
								);
								return Promise.reject('ValidationError');
							});
					} else {
						// Nothing changed. Return original order.
						return Promise.resolve(gearOrder);
					}
				},
				reset: () => {
					setSelectedOption({
						practitioner: gearOrder?.practitioner,
						office: gearOrder?.practitioner?.office
					});
				},
				isDirty: () => {
					const dirty = hasSelectionChanged(
						gearOrder?.practitioner?.gsdlNo,
						selectedOption?.practitioner?.gsdlNo
					);
					return dirty;
				}
			}),
			[gearOrder, selectedOption]
		);

		/**
		 * Load the available practitioner options for the user and enable the edit button if more
		 * than one practitioner option is available (or if the user is an admin).
		 */
		useEffect(() => {
			if (userInfo.type === 'myGDX' && gearOrder.patientRegistration && !gearOrder.practAgreement) {
				fetchPractitionerOptions(gearOrder.id)
					.then((options) => {
						setPractitionerOptions(options);
						const isAdmin = userInfo.adminUser === 'true';
						setShowEditButton(isAdmin || options?.length > 1);
					})
					.catch((error) => {
						// Ignore errors. The practitioner will not be editable.
					});
			} else {
				setShowEditButton(false);
			}
		}, [userInfo, gearOrder.id, gearOrder.patientRegistration, gearOrder.practAgreement]);

		/**
		 * Reset the selected option to the original value (the value from the gearOrder) when
		 * editing is canceled.
		 */
		useEffect(() => {
			if (!isEditing) {
				setSelectedOption({
					practitioner: gearOrder?.practitioner,
					office: gearOrder?.practitioner?.office
				});
				setError(null);
			} else {
				setChangesSaved(false);
			}
		}, [isEditing, gearOrder]);

		const practitioner = gearOrder?.practitioner;
		if (!practitioner) {
			return;
		}

		const office = practitioner?.office;
		const address = office?.address;

		let sectionBody;

		if (isEditing) {
			sectionBody = (
				<EditSection
					title="Change Practitioner"
					onCancel={onCancel}
					onSave={onSave}
					isEditSaveInProgress={isEditSaveInProgress}
				>
					<PractitionerSelection
						userInfo={userInfo}
						practitionerOptions={practitionerOptions}
						setPractitionerOptions={setPractitionerOptions}
						selectedOption={selectedOption}
						setSelectedOption={setSelectedOption}
						error={error}
					/>
				</EditSection>
			);
		} else {
			sectionBody = (
				<div>
					{office.name && <h4>{office.name}</h4>}
					<div>
						{practitioner.firstName + ' ' + practitioner.lastName}
						{practitioner.degree && ', ' + practitioner.degree} ({practitioner.gsdlNo})
					</div>
					{(userInfo.type === 'myGDX' && address) && (
						<div>
							{address.address1} <br />
							{address.address2 && (
								<span>
									{address.address2} <br />
								</span>
							)}
							<span>
								{address.city}, {address.state} {address.zipCode}
							</span>
						</div>
					)}
				</div>
			);
		}

		return (
			<SectionWell
				sectionId="practitionerInfo"
				title="Practitioner"
				onEdit={showEditButton && !isEditing && onEdit}
				changesSaved={changesSaved}
			>
				<>
					<div className="separator-2"></div>
					{sectionBody}
				</>
			</SectionWell>
		);
	}
);

/**
 * The "Edit Mode" of the Practitioner details.
 * This component displays a list of available practitioner options and allows the user to pick between the options.
 *
 * @param userInfo The logged in user
 * @param practitionerOptions The available pactitioner options
 * @param setPractitionerOptions A function to call to update the practitionerOptions list
 * @param selectedOption The currently selected practitioner option
 * @param setSelectedOption The function to call, passing the new option, when a new practitioner option is selected
 * @param error An error message to display
 */
const PractitionerSelection = ({
	userInfo,
	practitionerOptions,
	setPractitionerOptions,
	selectedOption,
	setSelectedOption,
	error
}) => {
	const updatePractitioner = (e) => {
		const gsdlNo = e.currentTarget.value;
		const selectedOption = practitionerOptions.find((option) => option.practitioner.gsdlNo === gsdlNo);
		setSelectedOption(selectedOption);
	};

	/**
	 * Push the practitioner into the list of practitioner options and set this as the selected practitioner.
	 * The push will only occurr if the practitioner is not already in the list.
	 *
	 * @param practitioner The practitioner details to select
	 */
	const handleAdminPractitionerSearchResults = (practitioner) => {
		if (!practitioner) {
			return;
		}

		// Convert the practitioner into a PractitionerOption
		const newOption = {
			practitioner: practitioner,
			office: practitioner.office
		};

		setSelectedOption(newOption);

		if (!practitionerOptions.some((option) => option.practitioner.gsdlNo === practitioner.gsdlNo)) {
			setPractitionerOptions((prevOptions) => [...prevOptions, newOption]);
		}
	};

	return (
		<>
			<div className="row">
				<div className="col-sm-8 col-md-6">
					<h4>Please select the ordering practitioner</h4>
					<form id="practitioner-form" noValidate>
						<select
							className="form-select"
							id="pract-select"
							value={selectedOption?.practitioner?.gsdlNo || ''}
							onChange={updatePractitioner}
						>
							{practitionerOptions.map(function (option) {
								return (
									<option key={option.practitioner.gsdlNo} value={option.practitioner.gsdlNo}>
										{option.practitioner.firstName} {option.practitioner.lastName},{' '}
										{option.practitioner.degree} ({option.practitioner.gsdlNo})
									</option>
								);
							})}
						</select>
					</form>
				</div>
			</div>

			{userInfo.adminUser === 'true' && (
				<div className="row mt-10">
					<div className="col-12">
						<div className="panel panel-info">
							<div className="panel-heading">Admin</div>
							<div className="panel-body">
								<label>Search for a practitioner by ID.</label>
								<br />
								<AdminPractitionerSearch onSubmit={handleAdminPractitionerSearchResults} />
							</div>
						</div>
					</div>
				</div>
			)}

			{error && <div className="validation-error text-danger">{error}</div>}
		</>
	);
};

/**
 * 'dirty' will be true when the order's practitioner does not match the selected practitioner
 */
const hasSelectionChanged = (orderGsdlNo, selectedGsdlNo) => {
	return orderGsdlNo !== selectedGsdlNo;
};

const saveSelection = (gearOrderId, gsdlNo) => {
	return changeOrderPractitioner(gearOrderId, gsdlNo);
};

PractitionerDetails.displayName = 'PractitionerDetails'; // DevTools display name
export default PractitionerDetails;
